import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import EventIcon from '@mui/icons-material/Event'
import PlaceIcon from '@mui/icons-material/Place'
import moment from 'moment'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'
import BigModal from '../../../components/Modals/BigModal'
import AnDateTimePicker from '../../../components/DateTime/DateTimePicker'
import { INTERVIEW_OUTCOMES, OUTCOME_COLORS } from '../stageMeta'

const StaffPicker = ({ label, value, onChange, options, helperText }) => (
  <Autocomplete
    multiple
    value={value}
    options={options}
    onChange={(e, newValue) => onChange(newValue)}
    isOptionEqualToValue={(option, selected) => option._id === selected._id}
    getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
    renderOption={(props, option) => (
      <Box component="li" {...props} key={option._id}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Avatar
            src={option.photograph}
            sx={{ width: 24, height: 24, borderRadius: 1 }}
          >
            {option.firstName?.[0]}
          </Avatar>
          {option.firstName} {option.lastName}
        </Stack>
      </Box>
    )}
    renderInput={(props) => (
      <TextField {...props} label={label} helperText={helperText} />
    )}
  />
)

const emptyForm = {
  title: '',
  scheduledStart: null,
  scheduledEnd: null,
  location: '',
  details: '',
  interviewers: [],
  present: [],
  notes: '',
  outcome: 'Pending',
  notifyApplicant: true,
}

const InterviewForm = ({ application, interview, onClose, onSaved }) => {
  const isEdit = !!interview
  const [form, setForm] = useState(
    isEdit
      ? {
          title: interview.title ?? '',
          scheduledStart: interview.scheduledStart ?? null,
          scheduledEnd: interview.scheduledEnd ?? null,
          location: interview.location ?? '',
          details: interview.details ?? '',
          interviewers: interview.interviewers ?? [],
          present: interview.present ?? [],
          notes: interview.notes ?? '',
          outcome: interview.outcome ?? 'Pending',
          notifyApplicant: false,
        }
      : emptyForm,
  )
  const [staffOptions, setStaffOptions] = useState([])
  const [saving, setSaving] = useState(false)
  const { setToast } = useContext(GlobalContext)

  useEffect(() => {
    API.post('/users-in-team')
      .then((res) => res.status === 200 && setStaffOptions(res.data))
      .catch((e) => console.log(e))
  }, [])

  const set = (field) => (value) =>
    setForm((prev) => ({ ...prev, [field]: value }))

  const handleSave = () => {
    if (!form.scheduledStart) {
      setToast({
        message: 'An interview date and time is required',
        severity: 'error',
      })
      return
    }
    setSaving(true)
    const payload = {
      title: form.title,
      scheduledStart: form.scheduledStart,
      scheduledEnd: form.scheduledEnd,
      location: form.location,
      details: form.details,
      interviewers: form.interviewers.map((person) => person._id),
      notifyApplicant: form.notifyApplicant,
    }
    if (isEdit) {
      payload.present = form.present.map((person) => person._id)
      payload.notes = form.notes
      payload.outcome = form.outcome
    }
    const request = isEdit
      ? API.patch(
          `/employees/applications/${application._id}/interviews/${interview._id}`,
          payload,
        )
      : API.post(`/employees/applications/${application._id}/interviews`, payload)
    request
      .then((res) => {
        if (res.status === 200) {
          setToast({
            message: isEdit ? 'Interview Updated' : 'Interview Scheduled',
          })
          onSaved()
          onClose()
        } else {
          setToast({
            message: res?.data?.error ?? 'Unable to save interview',
            severity: 'error',
          })
        }
      })
      .finally(() => setSaving(false))
  }

  return (
    <Stack gap={2}>
      <Typography variant="title2">
        {isEdit ? 'Edit Interview' : 'Schedule Interview'}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Title"
            fullWidth
            value={form.title}
            onChange={(e) => set('title')(e.target.value)}
            helperText="e.g. First Interview, Panel Interview"
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <AnDateTimePicker
            label="Start"
            fullWidth
            required
            value={form.scheduledStart}
            onChange={set('scheduledStart')}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <AnDateTimePicker
            label="Finish"
            fullWidth
            value={form.scheduledEnd}
            onChange={set('scheduledEnd')}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Location"
            fullWidth
            value={form.location}
            onChange={(e) => set('location')(e.target.value)}
            helperText="A room, address or video-call link — included in the applicant's invitation"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Details for the Applicant"
            fullWidth
            multiline
            minRows={2}
            value={form.details}
            onChange={(e) => set('details')(e.target.value)}
            helperText="Included in the invitation email, e.g. what to bring or who to ask for"
          />
        </Grid>
        <Grid item xs={12}>
          <StaffPicker
            label="Interviewers"
            value={form.interviewers}
            onChange={set('interviewers')}
            options={staffOptions}
          />
        </Grid>
        {isEdit && (
          <>
            <Grid item xs={12}>
              <Divider>Outcome</Divider>
            </Grid>
            <Grid item xs={12}>
              <StaffPicker
                label="Who was present"
                value={form.present}
                onChange={set('present')}
                options={staffOptions}
                helperText="Recorded alongside the outcome for the audit trail"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Interview Notes"
                fullWidth
                multiline
                minRows={3}
                value={form.notes}
                onChange={(e) => set('notes')(e.target.value)}
                helperText="Internal only — never shared with the applicant"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Outcome"
                select
                fullWidth
                value={form.outcome}
                onChange={(e) => set('outcome')(e.target.value)}
              >
                {INTERVIEW_OUTCOMES.map((outcome) => (
                  <MenuItem key={outcome} value={outcome}>
                    {outcome}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                color="success"
                checked={form.notifyApplicant}
                onChange={(e) => set('notifyApplicant')(e.target.checked)}
              />
            }
            label={
              isEdit
                ? 'Email the applicant about the new date/time (only sent if rescheduled)'
                : 'Email the applicant an interview invitation'
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Stack direction="row" gap={1} justifyContent="flex-end">
            <Button variant="outlined" color="error" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleSave}
              disabled={saving}
            >
              {isEdit ? 'Save Changes' : 'Schedule Interview'}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  )
}

const InterviewRow = ({ application, interview, onChanged, readOnly }) => {
  const { setToast } = useContext(GlobalContext)
  const [editOpen, setEditOpen] = useState(false)

  const handleRemove = () => {
    API.delete(
      `/employees/applications/${application._id}/interviews/${interview._id}`,
    ).then((res) => {
      if (res.status === 200) {
        setToast({ message: 'Interview Removed' })
        onChanged()
      }
    })
  }

  return (
    <Stack
      gap={0.5}
      padding={1.5}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        flexWrap="wrap"
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <EventIcon fontSize="small" color="action" />
          <Typography variant="title3">
            {interview.title || 'Interview'}
          </Typography>
          <Chip
            size="small"
            sx={{ borderRadius: 1 }}
            color={OUTCOME_COLORS[interview.outcome ?? 'Pending']}
            label={interview.outcome ?? 'Pending'}
          />
        </Stack>
        {!readOnly && (
          <Stack direction="row" gap={1}>
            <Button size="small" onClick={() => setEditOpen(true)}>
              {(interview.outcome ?? 'Pending') === 'Pending'
                ? 'Edit / Record Outcome'
                : 'Edit'}
            </Button>
            <Button size="small" color="error" onClick={handleRemove}>
              Remove
            </Button>
          </Stack>
        )}
      </Stack>
      <Typography variant="body2">
        {interview.scheduledStart
          ? moment(interview.scheduledStart).format('dddd Do MMM YYYY, HH:mm')
          : 'Time TBC'}
        {interview.scheduledEnd
          ? ` – ${moment(interview.scheduledEnd).format('HH:mm')}`
          : ''}
      </Typography>
      {interview.location && (
        <Stack direction="row" alignItems="center" gap={0.5}>
          <PlaceIcon fontSize="inherit" color="action" />
          <Typography variant="body2">{interview.location}</Typography>
        </Stack>
      )}
      {!!interview.interviewers?.length && (
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="caption" color="text.secondary">
            Interviewers:
          </Typography>
          <AvatarGroup
            max={6}
            sx={{ '& .MuiAvatar-root': { width: 22, height: 22, fontSize: 11 } }}
          >
            {interview.interviewers.map((person) => (
              <Tooltip
                key={person._id}
                title={`${person.firstName} ${person.lastName}`}
              >
                <Avatar src={person.photograph}>{person.firstName?.[0]}</Avatar>
              </Tooltip>
            ))}
          </AvatarGroup>
        </Stack>
      )}
      {!!interview.present?.length && (
        <Typography variant="caption" color="text.secondary">
          Present:{' '}
          {interview.present
            .map((person) => `${person.firstName} ${person.lastName}`)
            .join(', ')}
        </Typography>
      )}
      {interview.notes && (
        <Typography variant="body2" color="text.secondary">
          {interview.notes}
        </Typography>
      )}
      <BigModal open={editOpen} onClose={() => setEditOpen(false)}>
        {editOpen && (
          <InterviewForm
            application={application}
            interview={interview}
            onClose={() => setEditOpen(false)}
            onSaved={onChanged}
          />
        )}
      </BigModal>
    </Stack>
  )
}

// Interview management for a single application — scheduling, reschedules,
// recording who was present and the outcome. The pipeline stage shown to the
// manager and the applicant's tracker both derive from this data.
const Interviews = ({ application, getApplication }) => {
  const [createOpen, setCreateOpen] = useState(false)
  const readOnly = !['Draft', 'Submitted'].includes(application.state)
  const interviews = [...(application.interviews ?? [])].sort(
    (a, b) => new Date(a.scheduledStart ?? 0) - new Date(b.scheduledStart ?? 0),
  )

  return (
    <Stack gap={1} component={Paper} padding={1.5}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
      >
        <Typography variant="title3">Interviews</Typography>
        {!readOnly && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => setCreateOpen(true)}
          >
            Schedule Interview
          </Button>
        )}
      </Stack>
      {interviews.length === 0 ? (
        <Alert severity="info">
          No interviews scheduled yet.
          {!readOnly &&
            ' Use Schedule Interview to book one — the applicant can be emailed the invitation automatically, and their application tracker will update to "Interview Scheduled".'}
        </Alert>
      ) : (
        interviews.map((interview) => (
          <InterviewRow
            key={interview._id}
            application={application}
            interview={interview}
            onChanged={getApplication}
            readOnly={readOnly}
          />
        ))
      )}
      <BigModal open={createOpen} onClose={() => setCreateOpen(false)}>
        {createOpen && (
          <InterviewForm
            application={application}
            onClose={() => setCreateOpen(false)}
            onSaved={getApplication}
          />
        )}
      </BigModal>
    </Stack>
  )
}

export default Interviews
